/**
 * InvokeSolutionOverlay — full-canvas form overlay for "Set up solution run".
 *
 * Design: position:absolute inset:10px inside the canvas area (DR-1).
 * NOT the Foundation Modal (which is a fixed-position centered dialog).
 *
 * Controlled form: every field write goes straight to solutionRunStore.
 * debounce happens in useDebouncedRunState (consumed by SolutionRunCard).
 *
 * Uses Foundation Button and design tokens (Tailwind classes) throughout.
 */

import React, { useRef, useState } from 'react';
import { Button, Typography } from '@growthaxl/web-foundation';
import { shellEventBus } from '../../../shell/events/eventBus';
import { getSolutionRunStore } from '../store/solutionRunStore';
import type { InvokeSolutionPayload, PresetSelection, SolutionOption } from '../types/solutionRun';

interface InvokeSolutionOverlayProps {
  payload: InvokeSolutionPayload;
  onDiscard: () => void;
}

// ESA "sync" spark icon
const SyncBadge = (): React.ReactElement => (
  <span className="inline-flex items-center gap-[5px] bg-esa-accent-muted px-2 py-0.5 rounded-full">
    <svg width="11" height="11" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path
        d="M12 6.5 L13.4 11.6 L18.5 13 L13.4 14.4 L12 19.5 L10.6 14.4 L5.5 13 L10.6 11.6 Z"
        fill="currentColor"
      />
    </svg>
    <Typography variant="caption" component="span" color="purple" className="text-[10.5px] font-semibold">
      In sync with ESA
    </Typography>
  </span>
);

// Field label row
function FieldLabel({
  label,
  hint,
}: {
  label: string;
  hint?: string;
}): React.ReactElement {
  return (
    <div className="flex items-baseline gap-2 mb-2">
      <Typography variant="caption" component="span" color="secondary" className="font-semibold !capitalize">
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" component="span" color="tertiary" className="text-[10px]">
          {hint}
        </Typography>
      )}
    </div>
  );
}

// Small "Add" button
function AddButton({
  onClick,
  label,
}: {
  onClick: () => void;
  label: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-1.5 border border-dashed border-border-strong rounded-lg py-1.5 text-[11.5px] font-medium text-text-tertiary hover:text-esa-accent hover:border-esa-accent hover:bg-esa-accent-muted transition-colors cursor-pointer"
    >
      <svg
        width="11"
        height="11"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M8 3v10M3 8h10" />
      </svg>
      {label}
    </button>
  );
}

// Locked chip shown instead of the textarea when rows were pre-selected in the
// explorer grid. The user cannot edit or clear this — it is read-only data scope.
function SelectionChip({ selection }: { selection: PresetSelection }): React.ReactElement {
  const text = `${selection.count} record${selection.count === 1 ? '' : 's'} you selected`;
  return (
    <div
      data-testid="data-scope-selection-chip"
      className="flex items-center justify-between gap-2 rounded-lg border border-brand-weak bg-brand-weakest px-[10px] py-2"
    >
      <Typography variant="body2" component="span" color="purple" className="text-[12.5px] font-semibold">
        {text}
      </Typography>
      <Typography variant="caption" component="span" color="tertiary" className="text-[10px] whitespace-nowrap shrink-0">
        passed from the data explorer
      </Typography>
    </div>
  );
}

export function InvokeSolutionOverlay({
  payload,
  onDiscard,
}: InvokeSolutionOverlayProps): React.ReactElement {
  const store = getSolutionRunStore();

  // Subscribe to each reactive field individually to avoid new-object-per-render
  // equality failures (Zustand v5 uses Object.is by default).
  const solution = store((s) => s.solution);
  const dataScope = store((s) => s.dataScope);
  const objective = store((s) => s.objective);
  const guardrails = store((s) => s.guardrails);
  const presetSelection = store((s) => s.presetSelection);

  // Actions are stable function references — access via getState() in handlers
  // to avoid subscribing to the full store state object.

  const [newGuardrailText, setNewGuardrailText] = useState('');
  const [showGuardrailInput, setShowGuardrailInput] = useState(false);
  const guardrailInputRef = useRef<HTMLInputElement>(null);

  const handleSolutionChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const selected = payload.solutions.find((s: SolutionOption) => s.id === e.target.value);
    if (selected) store.getState().setSolution(selected);
  };

  const handleAddGuardrail = (): void => {
    const text = newGuardrailText.trim();
    if (text) {
      store.getState().addGuardrail(text);
      setNewGuardrailText('');
      setShowGuardrailInput(false);
    }
  };

  const handleDiscard = (): void => {
    shellEventBus.dispatch({ type: 'SOLUTION_RUN_CANCELLED' });
    onDiscard();
  };

  return (
    <div
      data-testid="invoke-solution-overlay"
      style={{ position: 'absolute', inset: '10px' }}
      className="z-[100] rounded-xl shadow-elevation-4 border border-border-default bg-surface flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="shrink-0 min-h-[48px] border-b border-border-default flex items-center gap-[10px] px-3.5 bg-surface-secondary">
        <span className="w-2 h-2 rounded-full bg-esa-accent shrink-0" />
        <Typography variant="subtitle2" component="span" className="font-bold">
          Set up solution run
        </Typography>
        <SyncBadge />
        <div className="flex-1" />
        <button
          type="button"
          data-testid="overlay-close-button"
          title="Close"
          onClick={handleDiscard}
          className="w-[30px] h-[30px] shrink-0 inline-flex items-center justify-center rounded-md cursor-pointer bg-surface border border-border-strong text-text-primary hover:bg-surface-hover transition-colors"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {/* Solution selector */}
        <div className="py-[13px] border-t border-border-default">
          <FieldLabel label="Solution" />
          <select
            data-testid="solution-select"
            value={solution?.id ?? ''}
            onChange={handleSolutionChange}
            className="w-full font-sans text-[12.5px] text-text-primary px-[10px] py-2 rounded-lg border border-border-default bg-surface outline-none cursor-pointer"
          >
            {payload.solutions.map((s: SolutionOption) => (
              <option key={s.id} value={s.id}>
                {s.description ? `${s.label} — ${s.description}` : s.label}
              </option>
            ))}
          </select>
        </div>

        {/* Data Scope */}
        <div className="py-[13px] border-t border-border-default">
          <div className="flex items-baseline gap-2 mb-2">
            <Typography variant="caption" component="span" color="secondary" className="font-semibold !capitalize">
              Data scope
            </Typography>
            <Typography variant="caption" component="span" color="tertiary" className="text-[10px]">
              Optional
            </Typography>
            {presetSelection && (
              <Typography variant="caption" component="span" color="tertiary" className="ml-auto text-[10px]">
                From your selection
              </Typography>
            )}
          </div>
          {presetSelection ? (
            <SelectionChip selection={presetSelection} />
          ) : (
            <textarea
              data-testid="data-scope-input"
              placeholder="Leave empty to run on the whole catalog, or describe the subset in words."
              rows={2}
              spellCheck={false}
              value={dataScope}
              onChange={(e) => store.getState().setDataScope(e.target.value)}
              className="w-full font-sans text-[12.5px] text-text-primary px-[10px] py-2 rounded-lg border border-border-default bg-surface outline-none resize-y"
            />
          )}
        </div>

        {/* Objective */}
        <div className="py-[13px] border-t border-border-default">
          <FieldLabel label="Objective" hint="What outcome do you want?" />
          <textarea
            data-testid="objective-input"
            placeholder={'What outcome do you want on this data? (e.g. "Walmart-ready Q4 catalog at DQ ≥ 90")'}
            rows={2}
            spellCheck={false}
            value={objective}
            onChange={(e) => store.getState().setObjective(e.target.value)}
            className="w-full font-sans text-[12.5px] text-text-primary px-[10px] py-2 rounded-lg border border-border-default bg-surface outline-none resize-y"
          />
        </div>

        {/* Guardrails */}
        <div className="py-[13px] border-t border-border-default">
          <FieldLabel label="Guardrails & constraints" hint="Optional" />
          <div className="flex flex-col gap-2">
            {guardrails.map((g) => (
              <div
                key={g.id}
                data-testid={`guardrail-item-${g.id}`}
                className="flex items-start gap-2 rounded-lg border border-border-default bg-surface-secondary h-[58px] px-[10px] py-[10px]"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-[7px] flex-wrap">
                    <Typography variant="body2" component="span" className="text-[12.5px] font-semibold h-[18px]">
                      {g.text}
                    </Typography>
                    {g.source === 'preset' && (
                      <Typography
                        variant="overline"
                        component="span"
                        color="purple"
                        data-testid={`guardrail-preset-badge-${g.id}`}
                        className="text-[9px] font-bold bg-esa-accent-muted px-[6px] py-[1px] rounded-full shrink-0"
                      >
                        Preset
                      </Typography>
                    )}
                  </div>
                </div>
                {g.source === 'user' && (
                  <button
                    type="button"
                    data-testid={`guardrail-remove-${g.id}`}
                    onClick={() => store.getState().removeGuardrail(g.id)}
                    className="text-text-tertiary hover:text-status-error transition-colors shrink-0"
                    aria-label={`Remove guardrail: ${g.text}`}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M4 4l8 8M12 4l-8 8" />
                    </svg>
                  </button>
                )}
              </div>
            ))}

            {/* Inline guardrail input */}
            {showGuardrailInput && (
              <div className="flex gap-2 items-center">
                <input
                  ref={guardrailInputRef}
                  data-testid="new-guardrail-input"
                  type="text"
                  placeholder="Enter a guardrail..."
                  value={newGuardrailText}
                  onChange={(e) => setNewGuardrailText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddGuardrail();
                    if (e.key === 'Escape') {
                      setShowGuardrailInput(false);
                      setNewGuardrailText('');
                    }
                  }}
                  className="flex-1 font-sans text-[12.5px] text-text-primary px-[10px] py-2 rounded-lg border border-border-default bg-surface outline-none"
                  autoFocus
                />
                <Button
                  variant="primary"
                  size="xs"
                  type="button"
                  onClick={handleAddGuardrail}
                  data-testid="confirm-guardrail-button"
                >
                  Add
                </Button>
              </div>
            )}

            <AddButton
              label="Add guardrail"
              onClick={() => {
                setShowGuardrailInput(true);
                setTimeout(() => guardrailInputRef.current?.focus(), 0);
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 border-t border-border-default px-[14px] py-[10px] flex items-center gap-2 bg-surface">
        <Button
          variant="outline"
          size="xs"
          type="button"
          onClick={handleDiscard}
          data-testid="discard-button"
        >
          Discard
        </Button>
        <div className="flex-1" />
        <Typography variant="caption" component="span" color="tertiary" className="text-[11px]">
          Changes sync to ESA live
        </Typography>
      </div>
    </div>
  );
}
